import type { Cell, QueryResultSet } from '@/lib/api/models'

function escapeCsvValue(value: Cell): string {
  if (value === null || value === undefined) return ''

  const text = String(value)
  // Quote when the value contains a delimiter, a quote, or a newline. A leading
  // =, +, - or @ is also quoted so spreadsheets don't evaluate it as a formula.
  if (/[",\n\r]/.test(text) || /^[=+\-@]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

/** Raw values, not display strings — an export should round-trip into Excel. */
export function toCsv(result: QueryResultSet): string {
  const header = result.columns.map((column) => escapeCsvValue(column)).join(',')
  const body = result.rows
    .map((row) => row.map((cell) => escapeCsvValue(cell)).join(','))
    .join('\r\n')

  return body ? `${header}\r\n${body}` : header
}

/** Tab-separated — what spreadsheets expect from a clipboard paste. */
export function toTsv(result: QueryResultSet): string {
  const clean = (value: Cell) =>
    value === null || value === undefined
      ? ''
      : String(value).replace(/\t/g, ' ').replace(/\r?\n/g, ' ')

  return [
    result.columns.map(clean).join('\t'),
    ...result.rows.map((row) => row.map(clean).join('\t')),
  ].join('\n')
}

export function toJson(result: QueryResultSet): string {
  const records = result.rows.map((row) =>
    Object.fromEntries(result.columns.map((column, index) => [column, row[index] ?? null])),
  )
  return JSON.stringify(records, null, 2)
}

export function csvFilename(query: string): string {
  const slug = query
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  return `qap-${slug || 'result'}-${stamp}.csv`
}
